import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Grid,
  Input,
  Progress,
  Row,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
  message as antdMessage,
} from 'antd';
import { CopyOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useSocketContext } from '../../sockets/SocketContext';

const { Paragraph, Text, Title } = Typography;

const STAGE_LABELS = {
  intake: 'Бриф',
  semantic: 'Смысловые варианты',
  phonetic: 'Фонетические варианты',
  metaphor: 'Метафоры',
  abbrev: 'Аббревиатуры',
  done: 'Итоговый отчёт',
};

const FUNNEL_STAGES = ['intake', 'semantic', 'phonetic', 'metaphor', 'abbrev', 'done'];
const VARIANT_STAGES = ['semantic', 'phonetic', 'metaphor', 'abbrev'];

const ACTION_LABELS = {
  start: 'Запускаю сессию и анализирую бриф',
  regenerate: 'Генерирую другие варианты',
  reset: 'Сбрасываю сессию',
};

const FIELD_LABELS = {
  activity: 'род деятельности',
  audience: 'целевая аудитория',
  currentName: 'текущее название',
  conflicts: 'конфликтные обозначения',
  productOrService: 'продукт или услуга',
  additionalComments: 'доп. комментарии',
};

const buildId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const formatDuration = (durationMs) => {
  if (!Number.isFinite(durationMs)) return null;

  if (durationMs < 1000) {
    return `${durationMs} мс`;
  }

  return `${(durationMs / 1000).toFixed(1)} с`;
};

const parseLines = (value) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const emitWithAck = (socket, eventName, payload) =>
  new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error('Сокет не подключен'));
      return;
    }

    if (!socket.connected) {
      reject(new Error('Нет активного подключения к серверу'));
      return;
    }

    socket.emit(eventName, payload, (response) => {
      if (!response) {
        reject(new Error('Сервер не вернул ответ'));
        return;
      }

      resolve(response);
    });
  });

const AiNamerTab = () => {
  const { socket } = useSocketContext();
  const socketStatus = useSelector((state) => state.interface.socketStatus);
  const screens = Grid.useBreakpoint();

  const [form] = Form.useForm();
  const hadExpertCheck = Form.useWatch('hadExpertCheck', form);
  const [messages, setMessages] = useState([]);
  const [selectedByMessage, setSelectedByMessage] = useState({});
  const [loadingAction, setLoadingAction] = useState(null);
  const [lastStage, setLastStage] = useState(null);
  const [isDone, setIsDone] = useState(false);

  const isSocketReady = socketStatus === 'connected' && socket;
  const isBusy = Boolean(loadingAction);
  const isMobile = !screens.md;

  const currentStageLabel = useMemo(() => {
    if (!lastStage) return null;
    return STAGE_LABELS[lastStage] || lastStage;
  }, [lastStage]);

  const activeVariantsMessageId = useMemo(() => {
    if (isDone) return null;

    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const item = messages[index];
      if (Array.isArray(item.variants) && item.variants.length > 0) {
        return item.id;
      }
    }

    return null;
  }, [messages, isDone]);

  const currentStageIndex = useMemo(() => {
    const index = FUNNEL_STAGES.indexOf(lastStage);
    if (index >= 0) return index;
    return 0;
  }, [lastStage]);

  const remainingSelections = useMemo(() => {
    const index = VARIANT_STAGES.indexOf(lastStage);
    if (index < 0) return 0;
    return VARIANT_STAGES.length - index;
  }, [lastStage]);

  const completedStageDurations = useMemo(
    () =>
      messages.filter(
        (item) =>
          item.role === 'assistant' &&
          item.stage &&
          item.durationMs &&
          (item.type === 'variants' || item.type === 'final_report')
      ),
    [messages]
  );

  const loadingText = useMemo(() => {
    if (!loadingAction) return '';
    if (loadingAction.startsWith('select-')) return 'Фиксирую выбор и формирую следующий этап';
    return ACTION_LABELS[loadingAction] || 'Идёт обработка запроса';
  }, [loadingAction]);

  const progressTagText = useMemo(() => {
    if (isDone) return 'Готово';
    if (lastStage === 'intake') return 'Нужно дополнить бриф';
    if (remainingSelections > 0) return `Осталось выборов: ${remainingSelections}`;
    return 'Ожидаю старт воронки';
  }, [isDone, lastStage, remainingSelections]);

  const appendAssistantResponse = (response, durationMs) => {
    const nextMessage = {
      id: buildId(),
      role: 'assistant',
      content: response.message || '',
      type: response.type,
      stage: response.stage,
      variants: Array.isArray(response.variants) ? response.variants : [],
      missingFields: Array.isArray(response.missing_fields) ? response.missing_fields : [],
      durationMs,
    };

    setMessages((prev) => [...prev, nextMessage]);
    setLastStage(response.stage || null);
    setIsDone(response.type === 'final_report' || response.stage === 'done');
  };

  const runSocketAction = async (actionName, eventName, payload, options = {}) => {
    setLoadingAction(actionName);
    const startedAt = performance.now();

    try {
      if (options.userText) {
        setMessages((prev) => [
          ...prev,
          { id: buildId(), role: 'user', content: options.userText },
        ]);
      }

      const response = await emitWithAck(socket, eventName, payload);

      if (response.status !== 'ok') {
        throw new Error(response.message || 'Сервер вернул ошибку');
      }

      appendAssistantResponse(response, Math.round(performance.now() - startedAt));
      return response;
    } catch (error) {
      antdMessage.error(error.message || 'Ошибка выполнения запроса');
      return null;
    } finally {
      setLoadingAction(null);
    }
  };

  const startSession = async () => {
    const values = form.getFieldsValue();
    const expertReviewDone = Boolean(values.hadExpertCheck);
    const brief = {
      activity: values.activity || '',
      audience: values.audience || '',
      currentName: values.currentName || '',
      conflicts: expertReviewDone ? parseLines(values.conflicts || '') : [],
      productOrService: values.productOrService || '',
      additionalComments: values.additionalComments || '',
      expertReviewDone,
      // Совместимость со старым контрактом: «экспертизы не было», если галка экспертизы не стоит
      noLegalResults: !expertReviewDone,
    };

    if (!brief.activity.trim() && !brief.productOrService.trim()) {
      antdMessage.error('Заполните род деятельности или продукт / услугу');
      return;
    }

    setMessages([]);
    setSelectedByMessage({});
    setLastStage(null);
    setIsDone(false);

    await runSocketAction('start', 'ai_namer_start', { brief });
  };

  const submitSelection = async (messageItem) => {
    const selected = selectedByMessage[messageItem.id] || [];

    if (selected.length === 0) {
      antdMessage.error('Выберите хотя бы один вариант');
      return;
    }

    await runSocketAction(
      `select-${messageItem.id}`,
      'ai_namer_select',
      { selected },
      { userText: `Выбрано: ${selected.join(', ')}` }
    );
  };

  const regenerateVariants = async () => {
    await runSocketAction('regenerate', 'ai_namer_regenerate', {});
  };

  const resetSession = async () => {
    setLoadingAction('reset');

    try {
      const response = await emitWithAck(socket, 'ai_namer_reset', {});
      if (response.status !== 'ok') {
        throw new Error(response.message || 'Не удалось сбросить сессию');
      }

      setMessages([]);
      setSelectedByMessage({});
      setLastStage(null);
      setIsDone(false);
      antdMessage.success(response.message || 'Сессия сброшена');
    } catch (error) {
      antdMessage.error(error.message || 'Ошибка сброса сессии');
    } finally {
      setLoadingAction(null);
    }
  };

  const copyFinalReport = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      antdMessage.success('Отчёт скопирован');
    } catch (error) {
      antdMessage.error('Не удалось скопировать отчёт');
    }
  };

  const renderFunnelProgress = () => {
    const progressPercent = Math.round((currentStageIndex / (FUNNEL_STAGES.length - 1)) * 100);

    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <Text strong>Прогресс воронки</Text>
            <Tag color={isDone ? 'green' : 'blue'}>{progressTagText}</Tag>
          </Space>

          <Steps
            size="small"
            current={currentStageIndex}
            direction={isMobile ? 'vertical' : 'horizontal'}
            responsive={false}
            items={FUNNEL_STAGES.map((stage) => ({
              title: STAGE_LABELS[stage],
              description:
                completedStageDurations.find((item) => item.stage === stage)?.durationMs
                  ? `за ${formatDuration(completedStageDurations.find((item) => item.stage === stage).durationMs)}`
                  : undefined,
            }))}
          />

          <Progress percent={progressPercent} size="small" status={isBusy ? 'active' : 'normal'} />
        </Space>
      </Card>
    );
  };

  const renderLoadingState = () => {
    if (!isBusy) return null;

    return (
      <Card
        size="small"
        className="theme-info-surface"
        style={{
          marginBottom: 12,
          borderColor: '#91caff',
        }}
      >
        <Space align="start" size={12}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 22 }} spin />} />
          <Space direction="vertical" size={4}>
            <Text strong>{loadingText}</Text>
            <Text type="secondary">
              Не закрывайте вкладку: сервер формирует ответ по текущему этапу.
            </Text>
            <Progress
              percent={Math.max(10, Math.min(95, currentStageIndex * 20 + 20))}
              size="small"
              status="active"
              showInfo={false}
              style={{ width: isMobile ? 220 : 360 }}
            />
          </Space>
        </Space>
      </Card>
    );
  };

  const renderVariants = (messageItem) => {
    const variants = Array.isArray(messageItem.variants) ? messageItem.variants : [];
    if (!variants.length) return null;

    const selected = selectedByMessage[messageItem.id] || [];
    const isActive = messageItem.id === activeVariantsMessageId;

    return (
      <div style={{ marginTop: 12 }}>
        {isActive && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Текущий шаг: ${STAGE_LABELS[messageItem.stage] || messageItem.stage}`}
            description={`Выберите один или несколько вариантов. После этого останется выборов: ${Math.max(
              remainingSelections - 1,
              0
            )}.`}
          />
        )}

        <Checkbox.Group
          value={selected}
          disabled={!isActive}
          onChange={(checkedValues) =>
            setSelectedByMessage((prev) => ({
              ...prev,
              [messageItem.id]: checkedValues,
            }))
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[12, 12]}>
            {variants.map((variant) => (
              <Col xs={24} sm={24} md={8} key={variant.name}>
                <Card size="small" style={{ height: '100%' }}>
                  <Checkbox value={variant.name}>
                    <Text strong>{variant.name}</Text>
                  </Checkbox>
                  {variant.rationale && (
                    <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                      {variant.rationale}
                    </Paragraph>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>

        <Space
          wrap
          direction={isMobile ? 'vertical' : 'horizontal'}
          style={{ marginTop: 12, width: '100%' }}
        >
          <Button
            type="primary"
            onClick={() => submitSelection(messageItem)}
            loading={loadingAction === `select-${messageItem.id}`}
            disabled={isBusy || !isActive || selected.length === 0}
            block={isMobile}
          >
            Далее
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={regenerateVariants}
            loading={loadingAction === 'regenerate'}
            disabled={isBusy || !isActive}
            block={isMobile}
          >
            Другие варианты
          </Button>
        </Space>
      </div>
    );
  };

  const renderMessage = (messageItem) => {
    const isAssistant = messageItem.role === 'assistant';
    const isFinalReport = messageItem.type === 'final_report' || messageItem.stage === 'done';
    const missingFields = Array.isArray(messageItem.missingFields)
      ? messageItem.missingFields
      : [];

    return (
      <Card
        key={messageItem.id}
        size="small"
        className={isAssistant ? 'theme-chat-user' : 'theme-chat-assistant'}
        style={{
          marginBottom: 12,
          borderColor: isAssistant ? undefined : '#91caff',
        }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space wrap>
            <Text strong>{isAssistant ? 'ИИ-Неймер' : 'Вы'}</Text>
            {messageItem.stage && (
              <Tag color={isFinalReport ? 'green' : 'blue'}>
                {STAGE_LABELS[messageItem.stage] || messageItem.stage}
              </Tag>
            )}
            {messageItem.type && <Tag>{messageItem.type}</Tag>}
            {messageItem.durationMs && (
              <Tag color="purple">сформировано за {formatDuration(messageItem.durationMs)}</Tag>
            )}
          </Space>

          {messageItem.content && (
            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
              {messageItem.content}
            </Paragraph>
          )}

          {missingFields.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Нужно дополнить бриф"
              description={missingFields
                .map((field) => FIELD_LABELS[field] || field)
                .join(', ')}
            />
          )}

          {renderVariants(messageItem)}

          {isFinalReport && messageItem.content && (
            <Button
              icon={<CopyOutlined />}
              onClick={() => copyFinalReport(messageItem.content)}
              block={isMobile}
            >
              Копировать отчёт
            </Button>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        ИИ-Неймер
      </Title>
      <Paragraph type="secondary">
        Заполните бриф и нажмите «Начать». Общение с ассистентом только через бриф, выбор
        вариантов и кнопку «Другие варианты» — свободного чата в интерфейсе нет.
      </Paragraph>

      {!isSocketReady && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Нет подключения к серверу"
          description="Дождитесь восстановления соединения, чтобы начать или продолжить сессию."
        />
      )}

      {messages.length > 0 && renderFunnelProgress()}

      <Row gutter={[isMobile ? 12 : 24, isMobile ? 12 : 24]} align="top">
        <Col xs={24} lg={9}>
          <Card title="Бриф" size="small">
            <Form
              form={form}
              layout="vertical"
              initialValues={{ hadExpertCheck: false }}
            >
              <Form.Item label="Род деятельности" name="activity">
                <Input.TextArea
                  placeholder="Например: сервис доставки фермерских продуктов"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
              <Form.Item label="Продукт или услуга" name="productOrService">
                <Input.TextArea
                  placeholder="Можно заполнить вместо рода деятельности"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
              <Form.Item
                label="Целевая аудитория"
                name="audience"
                extra={<Text type="secondary">Необязательное поле</Text>}
              >
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
              <Form.Item
                label="Текущее название для ребренда"
                name="currentName"
                tooltip={{
                  title:
                    'Если уже есть обозначение, которое нужно доработать — укажите его. Если нет, оставьте пустым.',
                }}
              >
                <Input placeholder="При необходимости" />
              </Form.Item>

              <Form.Item name="hadExpertCheck" valuePropName="checked">
                <Checkbox>Была экспертная проверка</Checkbox>
              </Form.Item>

              {hadExpertCheck ? (
                <Form.Item
                  label="Конфликтные обозначения"
                  name="conflicts"
                  tooltip={{
                    title:
                      'Опционально: при генерации вариантов будут учтены ограничения, чтобы не повторять эти обозначения.',
                  }}
                >
                  <Input.TextArea
                    placeholder="Каждое обозначение с новой строки"
                    autoSize={{ minRows: 2, maxRows: 5 }}
                  />
                </Form.Item>
              ) : null}

              <Form.Item
                label="Доп. комментарии"
                name="additionalComments"
                tooltip={{
                  title:
                    'Любая дополнительная информация от заказчика: передаётся на сервер в брифе и должна попадать в промпт к модели.',
                }}
              >
                <Input.TextArea
                  placeholder="Комментарии для ИИ из брифа заказчика"
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </Form.Item>

              <Space wrap direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
                <Button
                  type="primary"
                  onClick={startSession}
                  loading={loadingAction === 'start'}
                  disabled={!isSocketReady || isBusy}
                  block={isMobile}
                >
                  Начать
                </Button>
                <Button
                  onClick={resetSession}
                  loading={loadingAction === 'reset'}
                  disabled={!isSocketReady || isBusy}
                  block={isMobile}
                >
                  Начать заново
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card
            size="small"
            title={
              <Space wrap>
                <span>Ответы ассистента</span>
                {currentStageLabel && <Tag color="blue">{currentStageLabel}</Tag>}
              </Space>
            }
          >
            {messages.length === 0 ? (
              <Alert
                type="info"
                showIcon
                message="Сессия ещё не начата"
                description="Заполните минимальный бриф и нажмите «Начать»."
              />
            ) : (
              <div>{messages.map(renderMessage)}</div>
            )}

            {renderLoadingState()}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AiNamerTab;
